<template>
  <v-dialog v-model="internalShow" max-width="950" persistent scrollable>
    <v-card class="rounded-lg">
      <!-- Modal Header -->
      <v-card-title class="pa-4 primary white--text d-flex justify-space-between align-center">
        <div class="d-flex align-center">
          <v-avatar color="white" size="48" class="me-3 elevation-2">
            <v-img v-if="customer && customer.avatar_url" :src="customer.avatar_url"></v-img>
            <v-icon v-else color="primary" size="28">mdi-account</v-icon>
          </v-avatar>
          <div>
            <div class="text-h6 font-weight-bold white--text">
              {{ customer ? customer.name : 'بيانات العميل' }}
            </div>
            <div class="text-subtitle-2 white--text opacity-90" dir="ltr" style="text-align: right;">
              {{ customer ? (customer.phone_number || customer.tel_number || '—') : '' }}
            </div>
          </div>
        </div>
        <v-btn icon dark @click="closeModal">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-divider></v-divider>

      <!-- Modal Content -->
      <v-card-text class="pa-4" style="max-height: 75vh;">
        <!-- Skeleton Loading -->
        <div v-if="loading" class="text-center py-12">
          <v-progress-circular indeterminate color="primary" size="50"></v-progress-circular>
          <div class="mt-4 text-subtitle-1 grey--text text--darken-1 font-weight-medium">
            جاري تحميل ملف العميل وحجوزاته...
          </div>
        </div>

        <div v-else-if="error" class="pa-6 text-center">
          <v-alert type="error" text class="mb-4">{{ error }}</v-alert>
          <v-btn color="primary" @click="fetchCustomerProfile">إعادة المحاولة</v-btn>
        </div>

        <div v-else>
          <!-- Customer Metadata Sub-Header -->
          <v-card outlined class="mb-4 pa-3 grey lighten-5 rounded-lg">
            <v-row dense class="text-caption">
              <v-col cols="12" sm="4">
                <span class="grey--text text--darken-2 font-weight-bold">البريد الإلكتروني:</span>
                <span class="font-weight-medium ms-1">{{ customerData.email || 'غير محدد' }}</span>
              </v-col>
              <v-col cols="12" sm="4">
                <span class="grey--text text--darken-2 font-weight-bold">معرف العميل (ID):</span>
                <span class="font-weight-medium ms-1">#{{ customerData.id }}</span>
              </v-col>
              <v-col cols="12" sm="4">
                <span class="grey--text text--darken-2 font-weight-bold">تاريخ التسجيل:</span>
                <span class="font-weight-medium ms-1">{{ customerData.created_at || '—' }}</span>
              </v-col>
            </v-row>
          </v-card>

          <!-- 5 KPI Cards -->
          <v-row dense class="mb-4">
            <v-col cols="6" sm="4" md="2" class="flex-grow-1">
              <v-card outlined class="pa-3 text-center rounded-lg border-primary">
                <div class="text-caption font-weight-bold grey--text text--darken-1">إجمالي الحجوزات</div>
                <div class="text-h6 font-weight-black primary--text mt-1">{{ kpis.total_reservations || 0 }}</div>
              </v-card>
            </v-col>
            <v-col cols="6" sm="4" md="2" class="flex-grow-1">
              <v-card outlined class="pa-3 text-center rounded-lg border-info">
                <div class="text-caption font-weight-bold grey--text text--darken-1">الرحلات القادمة</div>
                <div class="text-h6 font-weight-black blue--text text--darken-2 mt-1">{{ kpis.upcoming_reservations || 0 }}</div>
              </v-card>
            </v-col>
            <v-col cols="6" sm="4" md="2" class="flex-grow-1">
              <v-card outlined class="pa-3 text-center rounded-lg border-success">
                <div class="text-caption font-weight-bold grey--text text--darken-1">المكتملة</div>
                <div class="text-h6 font-weight-black success--text mt-1">{{ kpis.completed_reservations || 0 }}</div>
              </v-card>
            </v-col>
            <v-col cols="6" sm="4" md="2" class="flex-grow-1">
              <v-card outlined class="pa-3 text-center rounded-lg border-error">
                <div class="text-caption font-weight-bold grey--text text--darken-1">الملغاة</div>
                <div class="text-h6 font-weight-black error--text mt-1">{{ kpis.cancelled_reservations || 0 }}</div>
              </v-card>
            </v-col>
            <v-col cols="12" sm="8" md="4" class="flex-grow-1">
              <v-card outlined class="pa-3 text-center rounded-lg deep-purple lighten-5 border-purple">
                <div class="text-caption font-weight-bold deep-purple--text text--darken-3">إجمالي المدفوعات</div>
                <div class="text-h6 font-weight-black deep-purple--text text--darken-4 mt-1">
                  {{ kpis.total_paid_amount || 0 }} <small class="text-caption font-weight-bold">ج.م</small>
                </div>
              </v-card>
            </v-col>
          </v-row>

          <!-- Tabs for History & Top Routes -->
          <v-tabs v-model="tab" class="mb-3" color="primary">
            <v-tab class="font-weight-bold">
              <v-icon left size="18">mdi-history</v-icon>
              سجل الحجوزات
            </v-tab>
            <v-tab class="font-weight-bold">
              <v-icon left size="18">mdi-map-marker-path</v-icon>
              المسارات الأكثر استخداماً
            </v-tab>
          </v-tabs>

          <v-tabs-items v-model="tab">
            <!-- TAB 1: BOOKING HISTORY TABLE -->
            <v-tab-item>
              <v-data-table
                :headers="historyHeaders"
                :items="bookingHistory.data || []"
                :loading="historyLoading"
                hide-default-footer
                class="elevation-0 border rounded-lg"
                no-data-text="لا يوجد سجل حجوزات لهذا العميل"
              >
                <template v-slot:item.planned_trip.route.name="{ item }">
                  <span class="font-weight-bold primary--text">
                    {{ (item.planned_trip && item.planned_trip.route) ? item.planned_trip.route.name : '—' }}
                  </span>
                </template>

                <template v-slot:item.planned_trip.driver.name="{ item }">
                  <div>
                    <span v-if="item.planned_trip && item.planned_trip.driver" class="font-weight-medium">
                      {{ item.planned_trip.driver.name }}
                    </span>
                    <span v-else class="grey--text">—</span>
                    <div class="text-caption grey--text" dir="ltr" style="text-align: right;">
                      {{ (item.planned_trip && item.planned_trip.driver && (item.planned_trip.driver.phone_number || item.planned_trip.driver.tel_number)) ? (item.planned_trip.driver.phone_number || item.planned_trip.driver.tel_number) : '' }}
                    </div>
                  </div>
                </template>

                <template v-slot:item.paid_price="{ item }">
                  <span class="font-weight-bold">{{ item.paid_price }} ج.م</span>
                </template>

                <template v-slot:item.boarding_status="{ item }">
                  <v-chip
                    x-small
                    :color="item.boarding_status === 'boarded' ? 'success' : (item.boarding_status === 'no_show' ? 'error' : 'warning')"
                    class="font-weight-bold white--text"
                  >
                    {{ item.boarding_status === 'boarded' ? 'صعد' : (item.boarding_status === 'no_show' ? 'لم يصعد' : 'منتظر') }}
                  </v-chip>
                </template>

                <template v-slot:item.ride_status="{ item }">
                  <v-chip
                    x-small
                    :color="getStatusColor(item.ride_status)"
                    class="font-weight-bold white--text"
                  >
                    {{ getStatusText(item.ride_status) }}
                  </v-chip>
                </template>

                <template v-slot:item.planned_trip.planned_date="{ item }">
                  <small class="font-weight-bold">{{ item.planned_trip ? item.planned_trip.planned_date : '—' }}</small>
                </template>
              </v-data-table>

              <!-- Pagination -->
              <div v-if="bookingHistory.last_page > 1" class="d-flex justify-center my-3">
                <v-pagination
                  v-model="historyPage"
                  :length="bookingHistory.last_page"
                  :total-visible="5"
                  @input="fetchCustomerProfile"
                ></v-pagination>
              </div>
            </v-tab-item>

            <!-- TAB 2: TOP ROUTES -->
            <v-tab-item>
              <v-card outlined class="rounded-lg pa-2">
                <v-list v-if="routesUsage && routesUsage.length > 0" two-line>
                  <v-list-item v-for="r in routesUsage" :key="r.id" class="border-bottom">
                    <v-list-item-avatar color="primary lighten-5">
                      <v-icon color="primary">mdi-bus</v-icon>
                    </v-list-item-avatar>
                    <v-list-item-content>
                      <v-list-item-title class="font-weight-bold text-subtitle-1">
                        {{ r.name }}
                      </v-list-item-title>
                      <v-list-item-subtitle class="grey--text">
                        المسار رقم #{{ r.id }}
                      </v-list-item-subtitle>
                    </v-list-item-content>
                    <v-list-item-action>
                      <v-chip color="primary" class="font-weight-bold white--text">
                        {{ r.total_bookings }} حجز
                      </v-chip>
                    </v-list-item-action>
                  </v-list-item>
                </v-list>
                <div v-else class="text-center py-6 grey--text">
                  لا توجد مسارات مسجلة لهذا العميل
                </div>
              </v-card>
            </v-tab-item>
          </v-tabs-items>
        </div>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions class="pa-3 bg-light">
        <v-spacer></v-spacer>
        <v-btn color="grey darken-1" text font-weight-bold @click="closeModal">إغلاق</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: "CustomerProfileModal",
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    customer: {
      type: Object,
      default: () => null,
    },
  },
  data() {
    return {
      tab: 0,
      loading: false,
      historyLoading: false,
      error: null,
      customerData: {},
      kpis: {},
      routesUsage: [],
      bookingHistory: {},
      historyPage: 1,
      historyHeaders: [
        { text: "رقم التذكرة", value: "ticket_number", sortable: false },
        { text: "تاريخ الرحلة", value: "planned_trip.planned_date", sortable: false },
        { text: "المسار", value: "planned_trip.route.name", sortable: false },
        { text: "من", value: "first_stop.name", sortable: false },
        { text: "إلى", value: "last_stop.name", sortable: false },
        { text: "السائق", value: "planned_trip.driver.name", sortable: false },
        { text: "المبلغ المدفوع", value: "paid_price", sortable: false },
        { text: "حالة الصعود", value: "boarding_status", sortable: false },
        { text: "حالة الرحلة", value: "ride_status", sortable: false },
      ],
    };
  },
  computed: {
    internalShow: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },
  },
  watch: {
    value(newVal) {
      if (newVal && this.customer && this.customer.id) {
        this.historyPage = 1;
        this.fetchCustomerProfile();
      }
    },
  },
  methods: {
    closeModal() {
      this.internalShow = false;
    },
    fetchCustomerProfile() {
      if (!this.customer || !this.customer.id) return;
      
      if (this.historyPage === 1) {
        this.loading = true;
      } else {
        this.historyLoading = true;
      }
      this.error = null;

      axios
        .get(`/reservations/customer-profile/${this.customer.id}?page=${this.historyPage}`)
        .then((response) => {
          if (response.data && response.data.success) {
            this.customerData = response.data.customer || {};
            this.kpis = response.data.kpis || {};
            this.routesUsage = response.data.routes_usage || [];
            this.bookingHistory = response.data.booking_history || {};
          }
        })
        .catch((err) => {
          console.error("Error fetching customer profile:", err);
          this.error = "تعذر تحميل ملف العميل. يرجى المحاولة لاحقاً.";
        })
        .finally(() => {
          this.loading = false;
          this.historyLoading = false;
        });
    },
    getStatusColor(status) {
      switch (parseInt(status)) {
        case 0: return "info";
        case 1: return "warning";
        case 2: return "grey";
        case 3: return "success";
        case 4: return "error";
        default: return "grey";
      }
    },
    getStatusText(status) {
      switch (parseInt(status)) {
        case 0: return "قادمة";
        case 1: return "في الطريق";
        case 2: return "فائتة";
        case 3: return "مكتملة";
        case 4: return "ملغاة";
        default: return "—";
      }
    },
  },
};
</script>

<style scoped>
.border-primary { border-top: 3px solid #673ab7 !important; }
.border-info { border-top: 3px solid #2196f3 !important; }
.border-success { border-top: 3px solid #4caf50 !important; }
.border-error { border-top: 3px solid #f44336 !important; }
.border-purple { border-top: 3px solid #512da8 !important; }
.border-bottom { border-bottom: 1px solid #e0e0e0; }
</style>
